import ServerStatusUI from "components/common/ServerStatusUI";
import RuleModal from "components/common/RuleModal";
import { GeneralRoomParams, Room } from "module/types";
import SocketProtocol from "network/SocketProtocol";
import SocketService from "network/SocketService";
import UserSession from "network/UserSession";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import "sass/views/RoomListContainer.scss";

function RoomListContainer() {
  const { t } = useTranslation();
  const location = useLocation();
  const state = location.state as GeneralRoomParams;
  const nameInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigator = useNavigate();
  const [rooms, setRooms] = useState<Room[]>();
  const [isRuleOpen, setIsRuleOpen] = useState(false);

  enum RoomState {
    WAITING = "waiting",
    INGAME = "ingame",
    PAUSE = "pause",
  }

  const getRoomList = () => {
    const id = state.product.id;
    if (id !== undefined) {
      SocketService.getInstance().emit(SocketProtocol.ROOM_LIST, id);
    }
  };

  const goHome = () => {
    navigator("/", { replace: true });
  };

  const createRoom = () => {
    // Check if username is entered
    const nickName = UserSession.getInstance().getUserNickName();
    if (!nickName || nickName === UserSession.getInstance().user.defaultNickName) {
      alert(t("usernameRequired"));
      return;
    }

    // Check if room name is entered
    if (inputRef.current?.value === "") {
      alert(t("roomNameRequired"));
      return;
    }

    const id = state.product.id;
    if (id !== undefined) {
      SocketService.getInstance().emit(
        SocketProtocol.CREATE_ROOM,
        id,
        inputRef.current?.value
      );
    }
  };

  const joinRoom = (roomName: string) => {
    // Check if username is entered
    const nickName = UserSession.getInstance().getUserNickName();
    if (!nickName || nickName === UserSession.getInstance().user.defaultNickName) {
      alert(t("usernameRequired"));
      return;
    }

    var room = "";
    if (roomName !== "") {
      room = roomName;
    } else {
      // Check if room name is entered when joining via input
      if (inputRef.current?.value === undefined || inputRef.current?.value === "") {
        alert(t("roomNameRequired"));
        return;
      }
      room = inputRef.current!.value;
    }
    const id = state.product.id;
    if (id !== undefined) {
      SocketService.getInstance().emit(
        SocketProtocol.JOIN_ROOM,
        id,
        room,
        UserSession.getInstance().getUserNickName()
      );
    }
  };

  const onClickRoom = (roomName: string) => {
    // Check if the room is in game
    const room = rooms?.find(r => r.roomName === roomName);
    if (room && room.state === RoomState.INGAME) {
      alert(t("roomInGame"));
      return;
    }
    joinRoom(roomName);
  };

  const onChangeNickName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget) {
      UserSession.getInstance().changeNickName(e.currentTarget.value);
    }
  };

  useEffect(() => {
    SocketService.getInstance().connect();
    if (state) {
      getRoomList();
    }
    SocketService.getInstance().listenError();

    SocketService.getInstance().on(
      SocketProtocol.ROOM_LIST,
      (rooms: Room[]) => {
        if (rooms) {
          setRooms(rooms);
        }
      }
    );

    SocketService.getInstance().on(
      SocketProtocol.CREATE_ROOM,
      (roomName: string) => {
        joinRoom(roomName);
      }
    );

    SocketService.getInstance().on(
      SocketProtocol.JOIN_ROOM,
      (roomName: string, id: number) => {
        UserSession.getInstance().setUserId(id);
        navigator("/game", {
          state: {
            product: state.product,
            room: { roomName: roomName },
          },
        });
      }
    );
    return () => {
      SocketService.getInstance().off(SocketProtocol.ROOM_LIST);
      SocketService.getInstance().off(SocketProtocol.CREATE_ROOM);
      SocketService.getInstance().off(SocketProtocol.JOIN_ROOM);
    };
  }, []);

  return (
    <div className="room-wrapper space-gray-theme">
      <div className="room-container">
        <ServerStatusUI />
        <div className="room-header">
          <div className="flex w-full justify-between">
            <button
              className="flex items-center room-back-button"
              onClick={goHome}
            >
              <i className="fas fa-arrow-left" />
              {t("backToHome")}
            </button>
            <button
              className="room-rule-button"
              onClick={() => setIsRuleOpen(!isRuleOpen)}
            >
              <i className="fas fa-book" />
              {t("howToPlay")}
            </button>
          </div>
          <div className="room-title">{t(`${state.product.title}.title`)}</div>
          <RuleModal
            isOpen={isRuleOpen}
            recreationId={state.product.id}
            inGame={false}
          />
        </div>
        <div className="room-body">
          <div className="room-menu-grid">
            <div className="room-input-wrapper">
              <i className="fas fa-user" />
              <input
                ref={nameInputRef}
                defaultValue={
                  UserSession.getInstance().user.nickName !== ""
                    ? UserSession.getInstance().user.nickName
                    : ""
                }
                onChange={onChangeNickName}
                placeholder={t("enterUsername")}
                className="room-input-name"
              ></input>
            </div>
            <div className="room-input-wrapper">
              <i className="fas fa-gamepad" />
              <input
                ref={inputRef}
                placeholder={t("enterRoomName")}
                className="room-input-room"
              ></input>
            </div>
            <div className="room-button-wrapper">
              <button className="room-button-create" onClick={createRoom}>
                <i className="fas fa-plus" />
                {t("createRoom")}
              </button>
            </div>
            <div className="room-button-wrapper">
              <button className="room-button-join" onClick={() => joinRoom("")}>
                <i className="fas fa-sign-in-alt" />
                {t("joinRoom")}
              </button>
            </div>
          </div>
          <div className="room-list">
            <div className="room-list-header">
              <div className="md:text-base text-sm">
                <button
                  className="px-1 rounded bg-gray-700 mr-2 hover:bg-gray-600"
                  onClick={getRoomList}
                >
                  <i className="fas fa-rotate-right fa-rotate-30" />
                </button>
                {t("room")}
              </div>
              <div className="md:text-base text-sm">{t("players")}</div>
              {/* <div className="text-sm">{t("private")}</div> */}
              <div className="md:text-base text-sm mr">{t("status")}</div>
            </div>
            <div className="overflow-scroll">
              {rooms &&
                rooms.map((room, id) => (
                  <div
                    className="room-list-item body"
                    key={id}
                    onClick={() => onClickRoom(room.roomName)}
                  >
                    <div className="md:text-base text-sm">{room.roomName}</div>
                    <div className="flex items-center gap-x-2">
                      <i className="fas fa-user text-gray-400 text-sm" />
                      <div className="md:text-base text-sm">
                        {room.userCount} / {state.product.attributes.maxUsers}
                      </div>
                    </div>
                    {/* <div className="pl-4">
                    {room.private ? (
                      <i className="fas fa-lock text-yellow-500">Yes</i>
                    ) : (
                      <i className="fas fa-lock-open text-green-500">No</i>
                    )}
                  </div> */}
                    <div className="flex align-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          room.state === RoomState.WAITING
                            ? "bg-green-900 text-green-400"
                            : "bg-yellow-900 text-yellow-400"
                        }`}
                      >
                        {room.state}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoomListContainer;
